import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils } from "@/comn/utils";
import { Page, Group, Layout, Button, Grid } from "@/comn/components";
import {
    useForm,
    useFetch,
    useGrid,
    useToast,
    useModal,
    useResource,
    useTree,
    usePage,
    TFormSchema,
} from "@/comn/hooks";
import {
    URLS,
    APIS,
    SF_ORG_USR_INFO,
    SG_USR_AUTH_LIST,
} from "@/tra/tancis/ptli/usr/orgusraprv/services/PtliOrgUsrAprvService";
import { PTLI1008003Q } from "@/tra/tancis/ptli/ptl/orgauth/PTLI1008003Q";

/*
 * Organization User Management Detail
 * !== 기관 사용자 관리 상세 조회 ==!
 */
export const PTLI0921002S = (props: any) => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const tree = useTree(); // Tree Hook !== Tree Hook ==!
    const { xtrnUsrUuid } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const [pTin, setPTin] = useState();

    useResource({
        defaultSchema: [{ area: "orgDeptCd" }, { area: "cntyCd" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Organization User Information Form !== 기관 사용자 정보 form ==!
        orgUsrInfo: useForm({
            defaultSchema: SF_ORG_USR_INFO,
            defaultValues: {},
        }),
    };

    const grid = {
        ogaUsrAuthList: useGrid({ defaultSchema: SG_USR_AUTH_LIST }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Check Organization User Information !== 기관 사용자 정보 조회 ==!
        getOrgUsrAprvInfo: useFetch({
            api: (data) => APIS.getOrgUsrAprvInfo(xtrnUsrUuid),
            enabled: !!xtrnUsrUuid,
            onSuccess: (data) => {
                const orgUsrInfo = data.orgUsrInfo.content;
                form.orgUsrInfo.setValues(orgUsrInfo);
                setPTin(orgUsrInfo.psnTin);

                // Organization User Auth list !== 기관 사용자 권한 목록 ==!
                const ogaUsrAuthList = orgUsrInfo.ogaUsrAuthList;
                if (ogaUsrAuthList != null) grid.ogaUsrAuthList.setData(comnUtils.getGridData(ogaUsrAuthList));
            },
            onError: (error) => {},
            showToast: true,
        }),

        // Check Organization User Auth list !== OGA사용자 권한 목록 조회 ==!
        getOgaUsrAuthList: useFetch({
            api: () => APIS.getOgaUsrAuthList(xtrnUsrUuid),
            onSuccess: (data) => {
                const ogaUsrAuthList = data.ogaUsrAuthList.content;
                if (ogaUsrAuthList != null) grid.ogaUsrAuthList.setData(comnUtils.getGridData(ogaUsrAuthList));
            },
            onError: () => modal.openModal({ content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),

        executeUsrAprv: useFetch({
            api: (data) => APIS.executeUsrAprv({ ...data }),
            //api: (data) => {console.log(data);},
            onSuccess: () => {
                const saveUsrAuthData = grid.ogaUsrAuthList.getData({ excludes: ["origin"] });
                if (!comnUtils.isEmpty(saveUsrAuthData)) {
                    fetch.saveOgaUsrAuthList.fetch();
                }
                modal.openModal({
                    content: "msg.com.00066", // Successfully approved., !== 정상적으로 승인되었습니다. ==!
                    onConfirm: () => {
                        modal.closeModal();
                        navigate(URLS.ptli0921001q);
                    },
                });
            },
            onError: (error) => {
                console.log(error);
                if (error.response.status === 400) {
                    console.log(error.response.data);
                    form.orgUsrInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: "msg.00006",
                });
            },
        }),

        executeUsrRtrn: useFetch({
            api: (data) => {
                return APIS.executeUsrRtrn(data);
            },
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.00003",
                    onConfirm: () => {
                        modal.closeModal();

                        navigate(URLS.ptli0921001q);
                    },
                });
            },
            onError: (error) => {
                if (error.response.status === 400) {
                    form.orgUsrInfo.setErrors(error.response.data.errors);
                }
                modal.openModal({
                    content: "msg.00006",
                });
            },
        }),

        // save Organization User Auth !== 기관 사용자 권한 저장 ==!
        saveOgaUsrAuthList: useFetch({
            api: () =>
                APIS.saveOgaUsrAuthList({
                    tin: pTin,
                    usrAuthList: grid.ogaUsrAuthList.getData({ excludes: ["origin"] }),
                }),
            onSuccess: () => {
                fetch.getOgaUsrAuthList.fetch();

                modal.openModal({ content: "msg.com.00062" }); // 정상적으로 저장되었습니다.
            },
            onError: () => modal.openModal({ content: "msg.com.00026" }), // Failed to save. !==  저장이 실패하였습니다. ==!
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Execute Organization User Approval !== 기관 사용자 직원 승인 ==!
        executeUsrAprv: () => {
            if (!comnUtils.isEmpty(form.orgUsrInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            form.orgUsrInfo.handleSubmit(
                () => {
                    const data = form.orgUsrInfo.getValues();
                    data.aprvYn = "Y";

                    const ogaUsrAuthList = grid.ogaUsrAuthList.getData();

                    const dataResult = { ...data, ogaUsrAuthList: ogaUsrAuthList };

                    modal.openModal({
                        content: "msg.com.00053", // Confirm approve? !== 승인하시겠습니까? ==!
                        onConfirm: () => {
                            console.log(dataResult);
                            fetch.executeUsrAprv.fetch(dataResult);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },

        // Execute Company Employee Return !== 업체 직원 반려 ==!
        executeEmpRtrn: () => {
            if (!comnUtils.isEmpty(form.orgUsrInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            form.orgUsrInfo.handleSubmit(
                () => {
                    const formData = form.orgUsrInfo.getValues();
                    formData.aprvOrgYn = aprvOrgYn;
                    formData.aprvYn = "N";

                    if (aprvOrgYn == "Y") {
                        const handlePopup = (data: any) => {
                            formData.rtrnRsn = data;

                            modal.closeModal();

                            fetch.executeUsrRtrn.fetch(formData);
                        };

                        modal.openModal({
                            content: <ModalRtrnRsn handlePopup={handlePopup} />,
                            size: "sm",
                            draggable: true,
                        });
                    } else {
                        formData.rtrnRsn = "Reject";
                        modal.openModal({
                            content: "msg.com.00052", // Confirm return? !== 반려하시겠습니까? ==!
                            onConfirm: () => {
                                fetch.executeUsrRtrn.fetch(formData);
                            },
                        });
                    }
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },

        onBtnSaveClick: () => {
            const list = grid.ogaUsrAuthList.getData({ excludes: ["origin"] });
            if (list.length === 0) {
                modal.openModal({ content: "msg.ptl.00741" }); // There is no data to be stored and deleted. !== 저장 및 삭제 대상 데이터가 없습니다. ==!
                return;
            }

            modal.openModal({
                content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                onConfirm: () => fetch.saveOgaUsrAuthList.fetch(),
            });
        },

        onBtnGrdAddRowClick: () => {
            const data = grid.ogaUsrAuthList.getData({ excludes: ["origin"] }); // INSERT, DELETE

            if (!comnUtils.isEmpty(data)) {
                modal.openModal({ content: "msg.ptl.00745" }); // Data has been changed. Please save it and proceed. !== 데이터가 변경되었습니다. 저장 후 진행해 주세요. ==!
                return;
            }

            const modalCallback = (list: any) => {
                const authList = grid.ogaUsrAuthList.getData();
                list.forEach(
                    (item: any) =>
                        authList.findIndex((subItem: any) => item.authId === subItem.authId) === -1 &&
                        grid.ogaUsrAuthList.addRow(item),
                );
                modal.closeModal();
            };

            modal.openModal({
                draggable: true,
                size: "md",
                content: (
                    <PTLI1008003Q tin={pTin} ogaUsrAuthList={grid.ogaUsrAuthList.getData()} callback={modalCallback} />
                ),
            });
        },

        onBtnGrdDelRowClick: () => {
            if (comnUtils.isEmpty(grid.ogaUsrAuthList.getChecked())) {
                modal.openModal({ content: "msg.com.00086" }); // No data has been selected.. !== 선택된 데이터가 없습니다. ==!
                return;
            }
            modal.openModal({
                content: "msg.com.00049",
                onConfirm: () => {
                    grid.ogaUsrAuthList.deleteRow("checkbox");
                },
            });
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        fetch.getOrgUsrAprvInfo.fetch();
        form.orgUsrInfo.setEditable(false);
    }, []);

    const aprvOrgYn = form.orgUsrInfo.watch("aprvOrgYn");

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.psnTin} />
                            <Group.Control {...form.orgUsrInfo.schema.orgCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.frgnrYn} />
                            <Group.Control {...form.orgUsrInfo.schema.aplcCntyCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.usrNm} />
                            <Group.Control {...form.orgUsrInfo.schema.usrMoblNo} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.usrEmlAddr} />
                            <Group.Control {...form.orgUsrInfo.schema.rsdtAddr} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Group.Title title={"T_OGA_USR_AUTH_LIST"} />
                    <Group.Section gap={2}>
                        <Layout.Right>
                            <Button role={"gridAdd"} onClick={handler.onBtnGrdAddRowClick} />
                            <Button role={"gridDelete"} onClick={handler.onBtnGrdDelRowClick} />
                        </Layout.Right>

                        <Grid {...grid.ogaUsrAuthList.grid} />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(`${URLS.ptli0921001q}?menuId=${menuId}`);
                        }}
                    ></Button>
                </Layout.Left>

                <Layout.Right>
                    <Layout.Button
                        color="blue"
                        onClick={() => {
                            handler.executeUsrAprv();
                        }}
                    >
                        {t("B_APRV")}
                    </Layout.Button>
                    <Layout.Button
                        color="error"
                        onClick={() => {
                            handler.executeEmpRtrn();
                        }}
                    >
                        {aprvOrgYn === "Y" ? t("B_RTRN") : t("B_RJCT")}
                    </Layout.Button>
                    <Button role={"save"} onClick={handler.onBtnSaveClick} />
                </Layout.Right>
            </Layout>
        </Page>
    );
};

/*
 * !== 반려사유 Modal ==!
 */
export const ModalRtrnRsn = (props: any) => {
    useResource({
        defaultSchema: [{ area: "bsopClsfCd" }, { area: "comnCd", comnCd: "COM0024" }],
    });

    const { data, handlePopup } = props;

    // 현재 화면설계 참고 -> 항목과 검색어 입력폼 명은 개발에 맞게 변경 해주세요(현재는 라벨이없어서 임시로 넣어놓았습니다)
    const SG_FORM: TFormSchema = {
        id: "form",
        schema: {
            rtrnRsn: { type: "textarea", label: "L_RTRN_RSN", required: true, controlSize: 10, maxLength: 2000 },
        },
    };

    const form = useForm({
        defaultSchema: SG_FORM,
        defaultValues: { ...data },
    });

    const handler = {
        submitBtnClick: () => {
            handlePopup(form.getValue("rtrnRsn"));
        },
    };

    return (
        <>
            <Page>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.schema.rtrnRsn} />
                            </Group.Row>
                        </Group.Section>
                        <Group.Section>
                            <Layout>
                                <Layout.Right>
                                    <Button
                                        role={"submit"}
                                        onClick={() => {
                                            handler.submitBtnClick();
                                        }}
                                    />
                                </Layout.Right>
                            </Layout>
                        </Group.Section>
                    </Group.Body>
                </Group>
            </Page>
        </>
    );
};
